import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

export const useWordsData = (groupId) => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadWords = useCallback(async () => {

    if (!groupId) {
  
      setWords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setError('User not authenticated');
        return;
      }

      // Fetch words with user's SRS data from word_srs_data table
      const { data, error: fetchError } = await supabase
        .from('words')
        .select(`
          id,
          group_id,
          from_text,
          to_text,
          position,
          created_at,
          updated_at,
          word_srs_data!left (
            sm2_interval,
            learning_interval,
          sm2_ease,
          learning_ease,
            next_due,
            apparent_tier,
            history,
            updated_at
          )
        `)
        .eq('group_id', groupId)
        .eq('word_srs_data.user_id', userId)
        .order('position', { ascending: true });

      if (fetchError) {
        setError('Error fetching words');
        return;
      }

      // Merge word data with SRS data, using defaults if SRS data doesn't exist
      const mergedWords = (data || []).map(word => {
        const srs = word.word_srs_data?.[0] || null;
        return {
          ...word,
          // Remove the nested word_srs_data array
          word_srs_data: undefined,
          // Add SRS fields directly to word object with defaults
          sm2_interval: srs?.sm2_interval ?? 0,
          learning_interval: srs?.learning_interval ?? 0,
          ease: srs?.sm2_ease ?? 2.5,
          next_due: srs?.next_due ?? 0,
          apparent_tier: srs?.apparent_tier ?? 1,
          history: srs?.history 
            ? (typeof srs.history === 'string' ? JSON.parse(srs.history) : srs.history)
            : [],
          srs_updated_at: srs?.updated_at || word.updated_at
        };
      });

      setWords(mergedWords);
    } catch (err) {
      setError('Error fetching words');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    loadWords();
  }, [loadWords]);

  return {
    words,
    loading,
    error,
    refetch: loadWords,
  };
};
